import { useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { usePrepress } from '../PrepressContext';
import PrepressWorkspace from '../components/PrepressWorkspace';

const PRODUCTS = ['Stand-up pouch', 'Flat bottom pouch', 'Stick pack', 'Shrink sleeve', 'Roll stock film', 'Label'];
const SOURCES = ['Portal upload', 'Email from CSR', 'Email from client', 'Sales handoff'];
const stClass = (s: string) => (s === 'released' ? 'st-completed' : s === 'on_hold' ? 'st-blocked' : s === 'approved' ? 'st-acknowledged' : s === 'intake' ? 'st-new' : 'st-in_progress');

export default function Prepress() {
  const { demo } = useAuth();
  const { data } = useAccounts(demo);
  const P = usePrepress();
  const clientNames = useMemo(() => (data?.accounts ?? []).filter((a) => a.type !== 'vendor').map((a) => a.name), [data]);

  const [client, setClient] = useState('');
  const [product, setProduct] = useState(PRODUCTS[0]);
  const [sku, setSku] = useState('');
  const [source, setSource] = useState(SOURCES[0]);
  const [openId, setOpenId] = useState<string | null>(null);

  const create = () => {
    if (!client.trim()) return;
    const id = P.createJob({ clientName: client, product, sku, source });
    setOpenId(id); setSku('');
  };

  const open = P.jobs.find((x) => x.id === openId) || null;
  const counts = {
    active: P.jobs.filter((j) => j.status !== 'released').length,
    review: P.jobs.filter((j) => j.status === 'client_review').length,
    hold: P.jobs.filter((j) => j.status === 'on_hold').length,
    released: P.jobs.filter((j) => j.status === 'released').length,
  };

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Prepress Workspace</div><div className="page-sub">Artwork → validate → proof → approve → blueprint → release. Technical readiness for every SKU.</div></div>
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Active Jobs</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{counts.active}</div><div className="metric-note">in prepress</div></div>
        <div className="metric-card"><div className="metric-label">Client Review</div><div className="metric-value" style={{ color: 'var(--yl)' }}>{counts.review}</div><div className="metric-note">awaiting approval</div></div>
        <div className="metric-card"><div className="metric-label">On Hold</div><div className="metric-value" style={{ color: 'var(--rd)' }}>{counts.hold}</div><div className="metric-note">technical issue</div></div>
        <div className="metric-card"><div className="metric-label">Released</div><div className="metric-value" style={{ color: 'var(--gn)' }}>{counts.released}</div><div className="metric-note">green-lit</div></div>
      </div>

      <div className="grid sidebar-layout">
        <div className="card">
          <div className="card-header"><div><h3>New Artwork Intake</h3><p>Log artwork received from client, CSR, sales, portal, or email.</p></div></div>
          <div className="card-body">
            <div className="form-grid">
              <div className="field"><label>Client</label><input list="pp-clients" value={client} onChange={(e) => setClient(e.target.value)} placeholder="Search client…" /><datalist id="pp-clients">{clientNames.slice(0, 200).map((n) => <option key={n} value={n} />)}</datalist></div>
              <div className="field"><label>SKU</label><input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU-…" /></div>
              <div className="field"><label>Product</label><select value={product} onChange={(e) => setProduct(e.target.value)}>{PRODUCTS.map((p) => <option key={p}>{p}</option>)}</select></div>
              <div className="field"><label>Source</label><select value={source} onChange={(e) => setSource(e.target.value)}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select></div>
            </div>
            <button className="btn primary" style={{ marginTop: 12 }} onClick={create}>Open prepress job →</button>
          </div>
        </div>
        <div className="card pad">
          <div className="section-h">Prepress Lifecycle</div>
          <ol className="lifecycle">
            {['Review intake', 'Validate files', 'Request info', 'Create proof', 'Send for review', 'Log revision', 'Compare versions', 'Approve internal', 'Capture client approval', 'Create blueprint', 'SKU record', 'Link dieline', 'Color standard', 'Plate/tooling', 'Release / hold', 'Archive', 'Log notes'].map((s, i) => (
              <li key={i}><span className="lc-n">{i + 1}</span>{s}</li>
            ))}
          </ol>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><div><h3>Prepress Queue</h3><p>Open any job to run validation, proofing, and release.</p></div></div>
        <div className="table-wrap"><table>
          <thead><tr><th>ID</th><th>Client</th><th>SKU</th><th>Product</th><th>Proofs</th><th>Status</th><th>Created</th></tr></thead>
          <tbody>
            {P.jobs.map((j) => (
              <tr key={j.id} onClick={() => setOpenId(j.id)}>
                <td><strong>{j.id}</strong></td>
                <td>{j.clientName}</td>
                <td className="mono" style={{ background: 'none', border: 'none', padding: '13px 16px' }}>{j.sku || '—'}</td>
                <td style={{ color: 'var(--tx2)' }}>{j.product}</td>
                <td>{j.proofs.length}</td>
                <td><span className={`tag ${stClass(j.status)}`}>{j.status.replace('_', ' ')}</span></td>
                <td>{j.created}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {open && <PrepressWorkspace job={open} onClose={() => setOpenId(null)} />}
    </>
  );
}
